<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] p-4 pb-24">
    <!-- LOADING -->
    <Loading v-if="loading" :text="$t('common.loading')" />

    <!-- CONTENT -->
    <div v-else class="space-y-6">
      <!-- Note Display/Edit Section -->
      <ModuleShell hideAction>
        <template #title>
          <div class="flex flex-wrap justify-between items-center w-full gap-2">
            <div class="flex items-center gap-2 min-w-0">
              <Notebook class="w-5 h-5 shrink-0" />
              <span class="truncate">{{ $t('views.notes.title') }}</span>
            </div>
            <div v-if="hasNote && !isEditing" class="flex gap-2 shrink-0">
              <button
                @click="startEdit"
                class="h-8 w-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer bg-[var(--color-primary-200)] text-[var(--color-primary-700)] hover:bg-[var(--color-primary-300)]"
                :title="$t('common.buttons.edit')">
                <Pencil class="w-4 h-4" />
              </button>
              <button
                @click="openDeleteDialog"
                class="h-8 w-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer bg-[var(--color-primary-200)] text-[var(--color-error)] hover:bg-[var(--color-error)] hover:text-white"
                :title="$t('common.buttons.delete')">
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
            <div v-if="isEditing || !hasNote" class="flex items-center gap-2 shrink-0">
              <button
                v-if="isEditing"
                @click="cancelEdit"
                :disabled="saving"
                class="btn btn--secondary h-[42px] px-3 sm:px-4 rounded-lg flex items-center gap-1 sm:gap-2 font-medium shadow-sm transition-transform active:scale-95 text-sm"
                :class="saving ? 'opacity-50 cursor-not-allowed' : ''">
                <span class="whitespace-nowrap">{{ $t('common.buttons.cancel') }}</span>
              </button>
              <button
                @click="saveNote"
                :disabled="!newNote.trim() || saving"
                class="btn h-[42px] px-3 sm:px-4 rounded-lg flex items-center gap-1 sm:gap-2 font-medium text-white shadow-sm transition-transform active:scale-95 text-sm"
                :class="!newNote.trim() || saving ? 'opacity-50 cursor-not-allowed' : ''"
                :style="{
                  backgroundColor: 'var(--color-success)',
                  border: '1px solid var(--color-success)'
                }">
                <span class="whitespace-nowrap">{{ saving ? $t('common.saving') : $t('common.buttons.save') }}</span>
              </button>
            </div>
          </div>
        </template>
        
        <!-- Show textarea when editing or no note exists -->
        <div v-if="isEditing || !hasNote">
          <textarea
            v-model="newNote"
            :placeholder="$t('views.notes.notePlaceholder')"
            rows="8"
            class="w-full min-w-0 p-3 rounded-lg border border-[var(--color-primary-400)] bg-[var(--color-primary-100)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-highlight)] resize-none text-sm sm:text-base"
          ></textarea>
        </div>
        
        <!-- Display note when not editing -->
        <div v-else class="p-4 bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] min-h-[120px]">
          <p class="text-sm whitespace-pre-wrap break-words">{{ goat?.notes }}</p>
        </div>
      </ModuleShell>
    </div>
  </div>

  <!-- Confirm Delete Modal -->
  <Teleport to="body">
    <ConfirmActionModule
      v-if="showConfirm"
      :message="$t('views.notes.deleteConfirm')"
      :confirmMessage="$t('common.buttons.delete')"
      variant="danger"
      @cancel="closeConfirm"
      @confirm="deleteConfirmed" />
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Notebook, Trash2, Pencil } from 'lucide-vue-next'
import Loading from '@/components/ui/feedback/Loading.vue'
import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
import { useGoatStore } from '@/stores/goat.store.js'
import { useUiStore } from '@/stores/ui.store.js'
import ConfirmActionModule from '@/components/actions/ConfirmAction.vue'

const { t } = useI18n()
const route = useRoute()
const goatStore = useGoatStore()
const uiStore = useUiStore()

const loading = ref(true)
const saving = ref(false)
const newNote = ref('')
const showConfirm = ref(false)
const isEditing = ref(false)

const goatId = computed(() => Number(route.params.id))
const goat = computed(() => goatStore.goats.find(g => g.id === goatId.value))

// Check if note exists
const hasNote = computed(() => {
  return goat.value?.notes && goat.value.notes.trim().length > 0
})

onMounted(async () => {
  loading.value = true
  
  try {
    // Fetch only the specific goat if not already loaded
    if (!goat.value) {
      const fetchedGoat = await goatStore.getGoatById(goatId.value)
      if (fetchedGoat) {
        // Add to store if not present
        const existing = goatStore.goats.find(g => g.id === goatId.value)
        if (!existing) {
          goatStore.goats.push(fetchedGoat)
        }
      }
    }
    
    // Set header after goat is loaded
    uiStore.setHeader({
      title: t('views.notes.title'),
      subtitle: goat.value?.eartag || goat.value?.name || '',
      showBack: true,
    })
  } catch (error) {
    console.error('Error loading goat:', error)
  } finally {
    loading.value = false
  }
})

// Save note (overwrites entire field)
async function saveNote() {
  if (!newNote.value.trim() || saving.value) return

  saving.value = true
  try {
    // Always overwrite the entire notes field
    await goatStore.updateGoat(goatId.value, {
      notes: newNote.value.trim()
    })
    
    // Exit edit mode and clear input
    isEditing.value = false
    newNote.value = ''
  } catch (error) {
    console.error('Error saving note:', error)
  } finally {
    saving.value = false
  }
}

// Start editing existing note
function startEdit() {
  newNote.value = goat.value?.notes || ''
  isEditing.value = true
}

// Cancel edit
function cancelEdit() {
  isEditing.value = false
  newNote.value = ''
}

// Delete note
function openDeleteDialog() {
  showConfirm.value = true
}

function closeConfirm() {
  showConfirm.value = false
}

async function deleteConfirmed() {
  saving.value = true
  try {
    // Set notes to null to delete
    await goatStore.updateGoat(goatId.value, {
      notes: null
    })
    closeConfirm()
  } catch (error) {
    console.error('Error deleting note:', error)
  } finally {
    saving.value = false
  }
}
</script>
